<template>
  <div class="min-h-screen bg-gray-50">
    <div class="max-w-3xl mx-auto px-4 py-10">
      <div
        v-if="loading"
        class="bg-white rounded-lg shadow-sm p-10 text-center text-gray-500"
      >
        <div class="flex justify-center mb-6">
          <img :src="logoUrl" alt="TripDay" class="h-16 sm:h-20 w-auto" />
        </div>
        Carregando…
      </div>

      <div
        v-else-if="loadError"
        class="bg-white rounded-lg shadow-sm p-8 text-center text-red-800"
      >
        <div class="flex justify-center mb-4">
          <img :src="logoUrl" alt="TripDay" class="h-16 sm:h-20 w-auto" />
        </div>
        {{ loadError }}
      </div>

      <template v-else-if="payload">
        <div class="bg-white rounded-lg shadow-sm p-6 mb-4">
          <div class="flex items-center gap-3 mb-4">
            <img :src="logoUrl" alt="TripDay" class="h-16 sm:h-20 w-auto" />
          </div>
          <h1 class="text-2xl font-bold text-gray-900">
            Rubrica e assinatura (proprietário)
          </h1>
          <p v-if="payload.person_name" class="mt-2 text-gray-600 text-sm">
            {{ payload.person_name }}
          </p>
          <p v-if="payload.message" class="mt-4 text-gray-700 leading-relaxed text-sm">
            {{ payload.message }}
          </p>
        </div>

        <div
          v-if="payload.link_expired"
          class="bg-amber-50 border border-amber-200 rounded-lg p-6 text-amber-900 text-sm"
        >
          <p class="font-medium">Link expirado</p>
          <p class="mt-2">{{ payload.message }}</p>
        </div>

        <div
          v-else-if="done"
          class="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-emerald-900 text-sm"
        >
          <p class="font-medium">Pronto</p>
          <p class="mt-2">{{ doneMessage }}</p>
        </div>

        <template v-else-if="showDrawPanel">
          <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-5">
            <h2 class="text-lg font-semibold text-gray-900">
              Rubrica e assinatura
            </h2>
            <p class="text-sm text-gray-600">
              Desenhe a <strong>rubrica</strong> e a <strong>assinatura</strong> nos quadros. Use o dedo ou o mouse.
            </p>
            <p
              v-if="signError"
              class="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
            >
              {{ signError }}
            </p>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div class="flex items-center justify-between mb-1">
                  <label class="text-sm font-medium text-gray-700">Rubrica</label>
                  <button
                    type="button"
                    class="text-xs text-indigo-600 hover:text-indigo-800"
                    @click="clearRubrica"
                  >
                    Limpar
                  </button>
                </div>
                <canvas
                  ref="rubricaCanvas"
                  class="w-full max-w-md touch-none border-2 border-gray-300 rounded-lg bg-white cursor-crosshair h-36"
                  @pointerdown.prevent="onPointerDown($event, 'rubrica')"
                  @pointermove.prevent="onPointerMove($event, 'rubrica')"
                  @pointerup.prevent="onPointerUp($event, 'rubrica')"
                  @pointerleave.prevent="onPointerUp($event, 'rubrica')"
                />
              </div>
              <div>
                <div class="flex items-center justify-between mb-1">
                  <label class="text-sm font-medium text-gray-700">Assinatura</label>
                  <button
                    type="button"
                    class="text-xs text-indigo-600 hover:text-indigo-800"
                    @click="clearAssinatura"
                  >
                    Limpar
                  </button>
                </div>
                <canvas
                  ref="assinaturaCanvas"
                  class="w-full max-w-md touch-none border-2 border-gray-300 rounded-lg bg-white cursor-crosshair h-36"
                  @pointerdown.prevent="onPointerDown($event, 'assinatura')"
                  @pointermove.prevent="onPointerMove($event, 'assinatura')"
                  @pointerup.prevent="onPointerUp($event, 'assinatura')"
                  @pointerleave.prevent="onPointerUp($event, 'assinatura')"
                />
              </div>
            </div>
            <button
              type="button"
              class="w-full sm:w-auto px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium text-sm disabled:opacity-50"
              :disabled="signSubmitting || !canSubmitDraw"
              @click="submitSign"
            >
              {{ signSubmitting ? 'Enviando…' : 'Salvar rubrica e assinatura' }}
            </button>
          </div>
        </template>

        <div
          v-else-if="showVerificationForm"
          class="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <h2 class="text-lg font-semibold text-gray-900 mb-1">
            Confirme sua identidade
          </h2>
          <p class="text-sm text-gray-600 mb-4">
            Informe o <strong>CPF/CNPJ</strong> ou o <strong>celular</strong> (com DDD) cadastrados no TripDay — pelo menos um dos dois.
          </p>
          <p
            v-if="verifyError"
            class="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4"
          >
            {{ verifyError }}
          </p>
          <form class="space-y-4" @submit.prevent="submitVerification">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">CPF/CNPJ</label>
              <input
                v-model.trim="verifyDocument"
                type="text"
                autocomplete="off"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                placeholder="Somente números ou com pontuação"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Celular (com DDD)</label>
              <input
                v-model.trim="verifyPhone"
                type="tel"
                autocomplete="tel"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                placeholder="Ex.: 11 99999-8888"
              />
            </div>
            <button
              type="submit"
              class="w-full sm:w-auto px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium text-sm disabled:opacity-50"
              :disabled="verifySubmitting"
            >
              {{ verifySubmitting ? 'Verificando…' : 'Continuar' }}
            </button>
          </form>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PublicOwnerContractDraw',
  props: {
    token: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      logoUrl: '/tripday-logo.png',
      loading: true,
      loadError: null,
      payload: null,
      verifyDocument: '',
      verifyPhone: '',
      verifyError: null,
      verifySubmitting: false,
      drawAccessSig: null,
      signError: null,
      signSubmitting: false,
      done: false,
      doneMessage: '',
      drawState: {
        rubrica: { active: false, ctx: null, dpr: 1, hasInk: false },
        assinatura: { active: false, ctx: null, dpr: 1, hasInk: false }
      },
      _drawResizeObserver: null
    }
  },
  computed: {
    showVerificationForm() {
      const p = this.payload
      if (!p || p.link_expired) return false
      return !this.drawAccessSig && !this.done
    },
    showDrawPanel() {
      const p = this.payload
      if (!p || p.link_expired || this.done) return false
      return !!this.drawAccessSig
    },
    canSubmitDraw() {
      return this.drawState.rubrica.hasInk && this.drawState.assinatura.hasInk
    }
  },
  watch: {
    token: {
      immediate: true,
      handler() {
        this.resetState()
        this.fetchPage()
      }
    },
    showDrawPanel(val) {
      if (val) {
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.initDrawings()
              this.bindDrawResizeObservers()
            })
          })
        })
      } else {
        this.unbindDrawResizeObservers()
      }
    }
  },
  beforeUnmount() {
    this.unbindDrawResizeObservers()
  },
  methods: {
    resetState() {
      this.verifyDocument = ''
      this.verifyPhone = ''
      this.verifyError = null
      this.drawAccessSig = null
      this.signError = null
      this.done = false
      this.doneMessage = ''
    },

    initDrawings() {
      ;[ 'rubricaCanvas', 'assinaturaCanvas' ].forEach((refName, idx) => {
        const el = this.$refs[refName]
        if (!el || typeof el.getContext !== 'function') return
        const key = idx === 0 ? 'rubrica' : 'assinatura'
        this.setupCanvas(el, key)
      })
    },

    bindDrawResizeObservers() {
      this.unbindDrawResizeObservers()
      if (typeof ResizeObserver === 'undefined') return
      const ro = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          const rub = this.$refs.rubricaCanvas
          const ass = this.$refs.assinaturaCanvas
          if (rub && this.canvasNeedsRelayout(rub)) this.setupCanvas(rub, 'rubrica')
          if (ass && this.canvasNeedsRelayout(ass)) this.setupCanvas(ass, 'assinatura')
        })
      })
      const rub = this.$refs.rubricaCanvas
      const ass = this.$refs.assinaturaCanvas
      if (rub) ro.observe(rub)
      if (ass) ro.observe(ass)
      this._drawResizeObserver = ro
    },

    unbindDrawResizeObservers() {
      if (this._drawResizeObserver) {
        this._drawResizeObserver.disconnect()
        this._drawResizeObserver = null
      }
    },

    canvasNeedsRelayout(canvas) {
      const dpr = window.devicePixelRatio || 1
      const cw = Math.max(1, canvas.clientWidth)
      const ch = Math.max(1, canvas.clientHeight)
      return canvas.width !== Math.floor(cw * dpr) || canvas.height !== Math.floor(ch * dpr)
    },

    setupCanvas(canvas, key) {
      const cw = Math.max(1, canvas.clientWidth)
      const ch = Math.max(1, canvas.clientHeight)
      const dpr = window.devicePixelRatio || 1
      canvas.width = Math.floor(cw * dpr)
      canvas.height = Math.floor(ch * dpr)
      const ctx = canvas.getContext('2d')
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.scale(dpr, dpr)
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, cw, ch)
      ctx.strokeStyle = '#111827'
      ctx.lineWidth = 2
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      this.drawState[key].ctx = ctx
      this.drawState[key].dpr = dpr
      this.drawState[key].hasInk = false
    },

    canvasPoint(canvas, evt) {
      if (evt.currentTarget === canvas && typeof evt.offsetX === 'number' && typeof evt.offsetY === 'number') {
        return { x: evt.offsetX, y: evt.offsetY }
      }
      const rect = canvas.getBoundingClientRect()
      const cw = canvas.clientWidth || rect.width
      const ch = canvas.clientHeight || rect.height
      return {
        x: ((evt.clientX - rect.left) / rect.width) * cw,
        y: ((evt.clientY - rect.top) / rect.height) * ch
      }
    },

    onPointerDown(evt, key) {
      const canvas = key === 'rubrica' ? this.$refs.rubricaCanvas : this.$refs.assinaturaCanvas
      if (!canvas) return
      try {
        evt.target.setPointerCapture(evt.pointerId)
      } catch (e) {
        /* ignore */
      }
      const st = this.drawState[key]
      st.active = true
      if (!st.ctx || this.canvasNeedsRelayout(canvas)) this.setupCanvas(canvas, key)
      const p = this.canvasPoint(canvas, evt)
      st.ctx.beginPath()
      st.ctx.moveTo(p.x, p.y)
    },

    onPointerMove(evt, key) {
      const st = this.drawState[key]
      if (!st.active) return
      const canvas = key === 'rubrica' ? this.$refs.rubricaCanvas : this.$refs.assinaturaCanvas
      if (!canvas || !st.ctx) return
      const p = this.canvasPoint(canvas, evt)
      st.ctx.lineTo(p.x, p.y)
      st.ctx.stroke()
      st.hasInk = true
    },

    onPointerUp(evt, key) {
      this.drawState[key].active = false
      try {
        evt.target.releasePointerCapture(evt.pointerId)
      } catch (e) {
        /* ignore */
      }
    },

    clearRubrica() {
      const canvas = this.$refs.rubricaCanvas
      if (canvas) this.setupCanvas(canvas, 'rubrica')
    },

    clearAssinatura() {
      const canvas = this.$refs.assinaturaCanvas
      if (canvas) this.setupCanvas(canvas, 'assinatura')
    },

    canvasToPngBlob(canvas) {
      return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'image/png', 0.92)
      })
    },

    async fetchPage() {
      const t = (this.token || '').trim()
      if (!t) {
        this.loading = false
        this.loadError = 'Link inválido.'
        return
      }

      this.loading = true
      this.loadError = null
      this.payload = null

      try {
        const res = await fetch(`${window.location.origin}/api/v1/public/owner_contract_draw/${encodeURIComponent(t)}`)
        const data = await res.json().catch(() => ({}))

        if (!res.ok) {
          this.loadError = Array.isArray(data.errors) ? data.errors.join(' ') : (data.errors || 'Não foi possível abrir o link.')
          return
        }

        if (!data.ok) {
          this.loadError = 'Não foi possível abrir o link.'
          return
        }

        this.payload = data
      } catch (e) {
        this.loadError = 'Erro de rede. Tente novamente.'
      } finally {
        this.loading = false
      }
    },

    async submitVerification() {
      const t = (this.token || '').trim()
      this.verifyError = null

      if (!this.verifyDocument && !this.verifyPhone) {
        this.verifyError = 'Informe o CPF/CNPJ ou o celular.'
        return
      }

      this.verifySubmitting = true
      try {
        const res = await fetch(
          `${window.location.origin}/api/v1/public/owner_contract_draw/${encodeURIComponent(t)}/verify`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              document: this.verifyDocument || undefined,
              phone: this.verifyPhone || undefined
            })
          }
        )
        const data = await res.json().catch(() => ({}))

        if (!res.ok) {
          const msg = Array.isArray(data.errors) ? data.errors.join(' ') : (data.errors || 'Não foi possível verificar.')
          this.verifyError = msg
          return
        }

        if (!data.ok || !data.access_sig) {
          this.verifyError = 'Não foi possível continuar. Tente novamente.'
          return
        }

        this.drawAccessSig = data.access_sig
      } catch (e) {
        this.verifyError = 'Erro de rede. Tente novamente.'
      } finally {
        this.verifySubmitting = false
      }
    },

    async submitSign() {
      const t = (this.token || '').trim()
      this.signError = null

      if (!this.drawAccessSig) {
        this.signError = 'Sessão expirada. Atualize a página e confirme seus dados novamente.'
        return
      }

      if (!this.canSubmitDraw) {
        this.signError = 'Desenhe a rubrica e a assinatura antes de enviar.'
        return
      }

      const rubCanvas = this.$refs.rubricaCanvas
      const sigCanvas = this.$refs.assinaturaCanvas
      if (!rubCanvas || !sigCanvas) {
        this.signError = 'Quadros não carregados.'
        return
      }

      const rubBlob = await this.canvasToPngBlob(rubCanvas)
      const assBlob = await this.canvasToPngBlob(sigCanvas)
      if (!rubBlob || !assBlob || rubBlob.size < 800 || assBlob.size < 800) {
        this.signError = 'Desenhe a rubrica e a assinatura nos quadros antes de enviar.'
        return
      }

      this.signSubmitting = true
      try {
        const fd = new FormData()
        fd.append('rubrica', rubBlob, 'rubrica.png')
        fd.append('assinatura', assBlob, 'assinatura.png')

        const q = new URLSearchParams({ sig: this.drawAccessSig })
        const res = await fetch(
          `${window.location.origin}/api/v1/public/owner_contract_draw/${encodeURIComponent(t)}/sign?${q.toString()}`,
          { method: 'POST', body: fd }
        )
        const data = await res.json().catch(() => ({}))

        if (!res.ok) {
          const msg = Array.isArray(data.errors) ? data.errors.join(' ') : (data.errors || 'Não foi possível salvar.')
          this.signError = msg
          return
        }

        this.done = true
        this.doneMessage = data.message || 'Rubrica e assinatura registradas com sucesso.'
        this.unbindDrawResizeObservers()
      } catch (e) {
        this.signError = 'Erro de rede. Tente novamente.'
      } finally {
        this.signSubmitting = false
      }
    }
  }
}
</script>
